import { useCallback, useRef, useState } from "react";

const GRAPHQL_ENDPOINTS = [
  "https://indexer.hyperindex.xyz/28644e9/v1/graphql",
  "https://indexer.hyperindex.xyz/84ea235/v1/graphql",
  "https://indexer.hyperindex.xyz/cbb287e/v1/graphql"
];

const OPENSEA_API_KEY = "d84a112179444c95a27f0dbd95703030";

// Hardcoded as requested
const HARDCODED_ID = "1_0x7104b3e9e6fbbb878ade511f360901646836381a_1";

// ---------- low-level fetch helpers ----------
async function gqlRequest(endpoint, query, variables = {}) {
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GraphQL HTTP ${res.status}: ${text}`);
  }

  const json = await res.json();
  if (json.errors && json.errors.length) {
    const msg = json.errors.map((e) => e.message).join(" | ");
    throw new Error(`GraphQL error: ${msg}`);
  }
  return json.data;
}

// Introspect fields on the Art type so we can fetch "all columns"
async function getArtFieldNames(endpoint) {
  const INTROSPECTION = /* GraphQL */ `
    query ArtFields {
      __type(name: "Art") {
        fields { name }
      }
    }
  `;
  const data = await gqlRequest(endpoint, INTROSPECTION);
  const fields = data?.__type?.fields?.map((f) => f.name) || [];
  // Be safe: always include id and image_url first if present
  const prioritized = ["id", "image_url"];
  const rest = fields.filter((f) => !prioritized.includes(f));
  return [...prioritized.filter((f) => fields.includes(f)), ...rest];
}

// Build a query string using all field names (selection set)
function buildArtByPkQuery(fieldNames) {
  const selection = fieldNames.join("\n      ");
  return `
    query ArtByPk($id: String!) {
      Art_by_pk(id: $id) {
        ${selection}
      }
    }
  `;
}

// ---------- OpenSea API fetch ----------
/**
 * Fetch data from OpenSea API for a given contract address
 * Tries multiple approaches to get the image URL
 */
async function fetchFromOpenSea(contractAddress) {
  try {
    const options = {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'x-api-key': OPENSEA_API_KEY
      }
    };
    
    console.log('🦊 Fetching from OpenSea:', contractAddress);
    
    // Try to fetch NFT data - get token 1 from the contract
    let response = await fetch(
      `https://api.opensea.io/api/v2/chain/ethereum/contract/${contractAddress}/nfts/1`,
      options
    );
    
    let data = null;
    
    if (response.ok) {
      data = await response.json();
      console.log('✅ OpenSea NFT response:', data);
    } else {
      // If that fails, try listing all NFTs
      console.log('⚠️ NFT endpoint failed, trying NFTs list...');
      response = await fetch(
        `https://api.opensea.io/api/v2/chain/ethereum/contract/${contractAddress}/nfts?limit=1`,
        options
      );
      
      if (response.ok) {
        data = await response.json();
        console.log('✅ OpenSea NFTs list response:', data);
      }
    }
    
    if (!data) {
      throw new Error(`OpenSea API error: ${response.status}`);
    }
    
    // Extract image URL from various possible locations in the response
    let imageUrl = null;
    
    // Try different paths based on response structure
    if (data.nft) {
      // Single NFT endpoint response
      imageUrl = data.nft.image_url || data.nft.display_image_url || data.nft.metadata?.image;
      console.log('📷 Extracted from nft:', imageUrl);
    } else if (data.nfts && data.nfts.length > 0) {
      // NFTs list response
      const firstNft = data.nfts[0];
      imageUrl = firstNft.image_url || firstNft.display_image_url || firstNft.metadata?.image;
      console.log('📷 Extracted from nfts[0]:', imageUrl);
    } else if (data.collection) {
      // Collection endpoint response
      imageUrl = data.collection.image_url || data.collection.banner_image_url;
      console.log('📷 Extracted from collection:', imageUrl);
    } else if (data.image_url) {
      // Direct image_url field
      imageUrl = data.image_url;
      console.log('📷 Extracted directly:', imageUrl);
    }
    
    console.log('🦊 OpenSea final image_url:', imageUrl);
    
    return {
      source: 'opensea',
      data: data,
      image_url: imageUrl
    };
  } catch (error) {
    console.error('❌ OpenSea fetch failed:', error);
    return { source: 'opensea', data: null, image_url: null, error: error.message };
  }
}

// ---------- GraphQL multi-endpoint fetch ----------
/**
 * Fetch from a single GraphQL endpoint with the given ID
 */
async function fetchFromGraphQLEndpoint(endpoint, id) {
  try {
    console.log(`🔗 Fetching from GraphQL: ${endpoint}`);
    const fieldNames = await getArtFieldNames(endpoint);
    const query = buildArtByPkQuery(fieldNames);
    const data = await gqlRequest(endpoint, query, { id });
    const art = data?.Art_by_pk ?? null;
    
    console.log(`✅ GraphQL response from ${endpoint}:`, art ? 'Has data' : 'No data', art?.image_url ? `Image: ${art.image_url}` : 'No image');
    
    return {
      source: endpoint,
      data: art,
      image_url: art?.image_url ?? null
    };
  } catch (error) {
    console.error(`❌ GraphQL fetch failed for ${endpoint}:`, error);
    return { source: endpoint, data: null, image_url: null, error: error.message };
  }
}

// ---------- Main multi-source fetch ----------
/**
 * Fetch artwork data from all sources (OpenSea + all GraphQL endpoints)
 * Prioritizes OpenSea image_url but stores all data
 * 
 * @param contractAddress - The contract address to search for
 * @returns Object with image_url (prioritized) and all source data
 */
export async function fetchArtworkFromAllSources(contractAddress) {
  // Format contract address for GraphQL endpoints
  const formattedId = `1_${contractAddress.toLowerCase()}_1`;
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🎨 Fetching artwork data for:', contractAddress);
  console.log('📝 Formatted GraphQL ID:', formattedId);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // Fetch from all sources in parallel
  const [openSeaResult, ...graphqlResults] = await Promise.all([
    fetchFromOpenSea(contractAddress),
    ...GRAPHQL_ENDPOINTS.map(endpoint => fetchFromGraphQLEndpoint(endpoint, formattedId))
  ]);
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('📊 Results Summary:');
  console.log('OpenSea:', openSeaResult.image_url ? '✅ Has image' : '❌ No image');
  graphqlResults.forEach((result, i) => {
    console.log(`GraphQL ${i + 1}:`, result.image_url ? '✅ Has image' : '❌ No image');
  });
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // Prioritize image_url: OpenSea first, then any GraphQL endpoint that has one
  let finalImageUrl = openSeaResult.image_url;
  
  if (!finalImageUrl) {
    for (const result of graphqlResults) {
      if (result.image_url) {
        finalImageUrl = result.image_url;
        console.log('✅ Using image from GraphQL:', result.source);
        break;
      }
    }
  } else {
    console.log('✅ Using image from OpenSea');
  }
  
  console.log('🖼️ Final image URL:', finalImageUrl || '❌ NONE FOUND');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  return {
    image_url: finalImageUrl,
    contractAddress,
    sources: {
      opensea: openSeaResult,
      graphql: graphqlResults
    },
    // Helper to get the first available data
    primaryData: openSeaResult.data || graphqlResults.find(r => r.data)?.data || null
  };
}

// ---------- Legacy functions (backward compatibility) ----------
/**
 * Fetch ALL fields for Art_by_pk for the given id from first endpoint.
 * Uses introspection once per call, then queries with the full selection set.
 */
export async function fetchArtByPkOnce(id = HARDCODED_ID) {
  const fieldNames = await getArtFieldNames(GRAPHQL_ENDPOINTS[0]);
  const query = buildArtByPkQuery(fieldNames);
  const data = await gqlRequest(GRAPHQL_ENDPOINTS[0], query, { id });
  return data?.Art_by_pk ?? null;
}

/**
 * Convenience: returns only the image_url (string | null).
 */
export async function getImageUrl(id = HARDCODED_ID) {
  // Fast path: try a focused query first; if it fails (schema mismatch), fall back to full fetch.
  const QUICK = `
    query ImageOnly($id: String!) {
      Art_by_pk(id: $id) { image_url }
    }
  `;
  try {
    const data = await gqlRequest(GRAPHQL_ENDPOINTS[0], QUICK, { id });
    return data?.Art_by_pk?.image_url ?? null;
  } catch {
    const art = await fetchArtByPkOnce(id);
    return art?.image_url ?? null;
  }
}

// ---------- React hook: run-on-trigger pattern ----------
/**
 * React hook that prepares a trigger-able fetch for Art_by_pk.
 * Returns { data, loading, error, run, imageUrl }.
 *
 * Example:
 *   const { data, loading, error, run, imageUrl } = useArtByPk();
 *   // call run() in a click handler to fetch on demand
 */
export function useArtByPk(id = HARDCODED_ID) {
  const [data, setData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fieldsCacheRef = useRef(null); // cache field list for subsequent triggers

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // cache field names between triggers for speed
      if (!fieldsCacheRef.current) {
        fieldsCacheRef.current = await getArtFieldNames(GRAPHQL_ENDPOINTS[0]);
      }
      const query = buildArtByPkQuery(fieldsCacheRef.current);
      const result = await gqlRequest(GRAPHQL_ENDPOINTS[0], query, { id });
      const art = result?.Art_by_pk ?? null;
      setData(art);
      setImageUrl(art?.image_url ?? null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setData(null);
      setImageUrl(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  return { data, imageUrl, loading, error, run };
}

/**
 * React hook for multi-source artwork fetching
 * Returns { artworkData, loading, error, fetchArtwork }
 */
export function useArtworkSearch() {
  const [artworkData, setArtworkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchArtwork = useCallback(async (contractAddress) => {
    if (!contractAddress) {
      setError(new Error('Contract address is required'));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchArtworkFromAllSources(contractAddress);
      setArtworkData(result);
      return result;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      setArtworkData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { artworkData, loading, error, fetchArtwork };
}